import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAdvertisementBannersList,
  deleteAdvertisementBanner,
} from "../../../services/advertisementBannersService";
import { paginateData, calculateTotalPages } from "../../../assets/js/script";
import TableHeader from "../../Common/TableComponent/TableHeader";
import TablesRow from "../../Common/TableComponent/TablesRow";
import { Pagination } from "../../Common/TableComponent/Pagination";
import TableToolbar from "../../Common/TableComponent/TableToolbar";
import { AddAdvertisement } from "./AddAdvertisement";
import { Loading } from "../../Common/OtherElements/Loading";
import Swal from "sweetalert2";
import { confirmDelete } from "../../Common/OtherElements/confirmDeleteClone";
import { TableDataStatusError } from "../../Common/OtherElements/TableDataStatusError";
import { handleErrors } from "../../../utils/errorHandler";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import allImages from "../../../assets/images-import";
import ComponentHeader from "../../Common/OtherElements/ComponentHeader";
import {
  ADVERTISEMENT_POSITION_OPTIONS,
  PAGE_TYPE_OPTIONS,
} from "./advertisementConstants";
import {
  getAdId,
  getAdvertisementName,
  getAdvertisementPosition,
  getPageType,
  getImageUrl,
  getStartDate,
  getEndDate,
  getRedirectUrl,
  formatDateTimeDisplay,
  calculateAdScheduleStatus,
  getStatusBadgeClass,
  getReferenceDisplay,
  getOptionLabel,
} from "./advertisementUtils";

const renderThumbnail = (url) => (
  <span className="xc-table-thumb-wrap" title="Advertisement image">
    <img
      src={url || allImages.DefultImage}
      alt="Advertisement"
      className="xc-table-thumb"
      loading="lazy"
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = allImages.DefultImage;
      }}
    />
  </span>
);

const renderScheduleStatusBadge = (item) => {
  const status = calculateAdScheduleStatus(
    getStartDate(item),
    getEndDate(item),
  );
  const badgeClass = getStatusBadgeClass(status);
  return <span className={`badge badge-soft-${badgeClass}`}>{status}</span>;
};

const renderNameCell = (item) => {
  const name = getAdvertisementName(item);
  const redirect = getRedirectUrl(item);
  const hasRedirect = redirect && redirect !== "-";
  const href = hasRedirect
    ? /^https?:\/\//i.test(redirect)
      ? redirect
      : `https://${redirect}`
    : "";
  return (
    <div>
      <span className="xc-cell-title">{name}</span>
      {hasRedirect ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="xc-cell-sub xc-cell-link"
          title={redirect}
        >
          {redirect}
        </a>
      ) : null}
    </div>
  );
};

const renderPill = (label) => <span className="xc-pill">{label || "—"}</span>;

const formatIndex = (n) => String(n).padStart(2, "0");

export const ManageAdvertisementsContent = () => {
  const [pageAccessDetails, setPageAccessDetails] = useState({});
  const pageLevelAccessUrl = "advertisements";
  const navigate = useNavigate();
  const { pageAccessData } = usePageLevelAccess(pageLevelAccessUrl);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [adsList, setAdsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAdvertisement, setSelectedAdvertisement] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.addAccess && !pageAccessData.viewAccess) {
        navigate("/404-error-page");
      } else {
        setPageAccessDetails(pageAccessData);
      }
    }
  }, [pageAccessData, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getAdvertisementBannersList();
      const list = response.data?.result || response.data || [];
      setAdsList(Array.isArray(list) ? list : []);
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredData = adsList.filter((item) => {
    const name = getAdvertisementName(item).toLowerCase();
    const position = getAdvertisementPosition(item).toLowerCase();
    const pageType = getPageType(item).toLowerCase();
    const reference = getReferenceDisplay(item).toLowerCase();
    const redirect = getRedirectUrl(item).toLowerCase();
    const query = searchQuery.toLowerCase();
    return (
      name.includes(query) ||
      position.includes(query) ||
      pageType.includes(query) ||
      reference.includes(query) ||
      redirect.includes(query)
    );
  });

  const currentData = paginateData(filteredData, currentPage, entriesPerPage);
  const totalPages = calculateTotalPages(filteredData.length, entriesPerPage);

  const handleEntriesChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleDelete = async (adId) => {
    const confirmed = await confirmDelete("Advertisement");
    if (confirmed) {
      try {
        await deleteAdvertisementBanner(adId);
        Swal.fire(
          "Deleted!",
          "The advertisement has been deleted successfully.",
          "success",
        );
        fetchData();
      } catch (error) {
        handleErrors(error);
      }
    }
  };

  return (
    <>
      <ComponentHeader title="Advertisement Banners" />

      {pageAccessDetails.addAccess ? (
        <AddAdvertisement
          editMode={editMode}
          initialData={selectedAdvertisement ?? {}}
          onSuccess={fetchData}
          setSelectedAdvertisement={setSelectedAdvertisement}
          setEditMode={setEditMode}
          isOpen={isFormOpen}
          onClose={() => setIsFormOpen(false)}
        />
      ) : null}

      {pageAccessDetails.viewAccess ? (
        <div className="row">
          <div className="col-xxl-12">
            <div className="card mt-xxl-n5 xc-list-card">
              <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div>
                  <h5 className="mb-0">Manage Advertisements</h5>
                  <span className="xc-list-count">
                    {filteredData.length}{" "}
                    {filteredData.length === 1
                      ? "advertisement"
                      : "advertisements"}
                  </span>
                </div>
                {pageAccessDetails.addAccess ? (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      setSelectedAdvertisement(null);
                      setEditMode(false);
                      setIsFormOpen(true);
                    }}
                  >
                    <i className="ri-add-line me-1" aria-hidden="true" />
                    Add Advertisement
                  </button>
                ) : null}
              </div>
              <div className="card-body manage-amenity-master-card-body">
                <TableToolbar
                  entriesPerPage={entriesPerPage}
                  onEntriesChange={handleEntriesChange}
                  searchValue={searchQuery}
                  searchPlaceholder="Search advertisements..."
                  onSearchChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  onRefresh={fetchData}
                />

                {loading ? (
                  <Loading />
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle table-bordered">
                      <TableHeader
                        columns={[
                          "Sl No",
                          "Image",
                          "Advertisement Name",
                          "Position",
                          "Page Type",
                          "Start Date",
                          "End Date",
                          "Status",
                          "Action",
                        ]}
                      />
                      <tbody className="manage-page-group-table-values">
                        {currentData.length === 0 ? (
                          <TableDataStatusError colspan="9" />
                        ) : (
                          currentData.map((item, index) => {
                            const slNo =
                              (currentPage - 1) * entriesPerPage + index + 1;
                            return (
                              <TablesRow
                                key={getAdId(item)}
                                rowData={{
                                  slNo: (
                                    <span className="xc-cell-index">
                                      {formatIndex(slNo)}
                                    </span>
                                  ),
                                  image: renderThumbnail(getImageUrl(item)),
                                  advertisementName: renderNameCell(item),
                                  position: renderPill(
                                    getOptionLabel(
                                      ADVERTISEMENT_POSITION_OPTIONS,
                                      getAdvertisementPosition(item),
                                    ),
                                  ),
                                  pageType: renderPill(
                                    getOptionLabel(
                                      PAGE_TYPE_OPTIONS,
                                      getPageType(item),
                                    ),
                                  ),
                                  startDate: formatDateTimeDisplay(
                                    getStartDate(item),
                                  ),
                                  endDate: formatDateTimeDisplay(
                                    getEndDate(item),
                                  ),
                                  status: renderScheduleStatusBadge(item),
                                }}
                                columns={[
                                  "slNo",
                                  "image",
                                  "advertisementName",
                                  "position",
                                  "pageType",
                                  "startDate",
                                  "endDate",
                                  "status",
                                ]}
                                hideIcons={false}
                                onEdit={() => {
                                  setSelectedAdvertisement(item);
                                  setEditMode(true);
                                  setIsFormOpen(true);
                                }}
                                onDelete={() => handleDelete(getAdId(item))}
                                pageLevelAccessData={pageAccessDetails}
                              />
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalEntries={filteredData.length}
                  entriesPerPage={entriesPerPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        ""
      )}
    </>
  );
};
